import { ComponentType, lazy } from "react";

/**
 * Wrapper around React.lazy that retries failed chunk loads
 * If all retries fail, reloads the page to get the latest version
 *
 * This handles the case where:
 * 1. User has old app version open
 * 2. New deployment happens with different chunk names
 * 3. User navigates and tries to load a chunk that no longer exists
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyWithRetry<T extends ComponentType<any>>(
  importFunc: () => Promise<{ default: T }>,
  retries = 3,
  retryDelay = 1000,
): ReturnType<typeof lazy<T>> {
  return lazy(() => {
    return new Promise<{ default: T }>((resolve, reject) => {
      const attemptImport = (attemptNumber: number) => {
        importFunc()
          .then(resolve)
          .catch((error) => {
            // Check if this is a chunk loading error
            const isChunkLoadError =
              error?.name === "ChunkLoadError" ||
              error?.message?.includes(
                "Failed to fetch dynamically imported module",
              ) ||
              error?.message?.includes("Importing a module script failed");

            if (isChunkLoadError && attemptNumber < retries) {
              // Retry after delay
              console.warn(
                `[lazyWithRetry] Chunk load failed, retrying (${attemptNumber + 1}/${retries})...`,
              );
              setTimeout(() => {
                attemptImport(attemptNumber + 1);
              }, retryDelay);
            } else if (isChunkLoadError && attemptNumber >= retries) {
              // All retries failed - reload the page to get the latest version
              console.error(
                `[lazyWithRetry] All retries failed. Reloading page to get latest version...`,
              );
              window.location.reload();
            } else {
              // Not a chunk load error, reject normally
              reject(error);
            }
          });
      };

      attemptImport(0);
    });
  });
}
